namespace $ {
	
	export function $giper_baza_link_compare( left: $giper_baza_link, right: $giper_baza_link ) {
		return ( right.str > left.str ? 1 : right.str < left.str ? -1 : 0 )
	}
	
	export class $giper_baza_link extends Object {
		
		constructor( readonly str: string ) {
			super()
			
			if( !/^(([a-zæA-ZÆ0-9]{8})?_){0,3}([a-zæA-ZÆ0-9]{8})?$/.test( str ) ) {
				$mol_fail( new Error( `Wrong Link (${str})` ) )
			}
		
			this.str = str.replace( /AAAAAAAA/g, '' ).replace( /_+$/, '' )
		}
		
		static hole = new this( '' )
		
		static check( val: string ) {
			try {
				new this( val )
				return val
			} catch {
				return null
			}
		}
		
		[ $mol_key_handle ]() {
			return this.str
		}
		
		toString() {
			return this.str
		}
		
		toJSON() {
			return this.str
		}
		
		[ Symbol.toPrimitive ]() {
			return this.str
		}
		
		[ $mol_dev_format_head ]() {
			return $mol_dev_format_span( { 'color': 'darkorange' }, this.str || '_' )
		}
		
		_bin = null as null | Uint8Array< ArrayBuffer >
		
		/** Binary representation (6/12/18/24 bytes). */
		toBin(): Uint8Array< ArrayBuffer > {
			if( this._bin ) return this._bin
			const str = this.relate( _base ).str
			const norm = str && str
				.replace( /^___/, '' )
				.split( '_' )
				.map( numb => numb || 'AAAAAAAA' )
				.join( '' )
			return this._bin = $mol_base64_ae_decode( norm )
		}
		
		/** Make from integer (6 bytes). */
		static from_int( int: number ) {
			const bin = new Uint8Array( new BigUint64Array([ BigInt( int ) ]).buffer, 0, 6 )
			const link =  new this( $mol_base64_ae_encode( bin ) )
			link._bin = bin
			return link
		}
		
		/** Read from binary (6/12/18/24 bytes). */
		static from_bin( bin: Uint8Array< ArrayBuffer > ) {
			const str = [ ... $mol_base64_ae_encode( bin ).match( /(.{8})/g ) ?? [] ].join( '_' )
			const link = new this( str ).resolve( _base )
			link._bin = bin
			return link
		}
		
		static _hash_cache = new WeakMap< ArrayBufferView, $giper_baza_link >()
		
		/** Make hash from binary (12 bytes). */
		static hash_bin( bin: ArrayBufferView ) {
			
			let link = this._hash_cache.get( bin )
			if( link ) return link
			
			const hash = $mol_crypto_hash( bin )
			link = this.from_bin( new Uint8Array( hash.buffer, 0, 12 ) )
			this._hash_cache.set( bin, link )
			
			return link
		}
		
		/** Make hash from string (12 bytes). */
		static hash_str( str: string ) {
			return this.hash_bin( $mol_charset_encode( str ) )
		}
		
		/** Land-local Peer id. */
		peer() {
			return new $giper_baza_link( this.str.split( '_' )[ 0 ] ?? '' )
		}

		/** Lord-local Area id. */
		area() {
			return new $giper_baza_link( this.str.split( '_' )[ 2 ] ?? '' )
		}
		
		/** Land-local Head id. */
		head() {
			return new $giper_baza_link( this.str.split( '_' )[ 3 ] ?? '' )
		}
		
		/** Link to Lord Home. */
		lord() {
			return new $giper_baza_link( this.str.split( '_' ).slice( 0, 2 ).join( '_' ) )
		}
		
		/** Link to Land Root. */
		land() {
			return new $giper_baza_link( this.str.split( '_' ).slice( 0, 3 ).join( '_' ) )
		}
		
		/** Pawn Link relative to base Land: `___QWERTYUI` */
		relate( base: $giper_baza_link ) {
			if( base.str === '' ) return this
			base = base.land()
			if( this.land().str !== base.str ) return this
			const head = this.head()
			return new $giper_baza_link(  '___' + head )
		}

		/** Absolute Pawn Link from relative (`___QWERTYUI`) using base Land Link. */
		resolve( base: $giper_baza_link ) {
			
			if( base.str === '' ) return this
			if( this.str === '' ) return base.land()
			if( this.str.length > 16 ) return this
			
			const parts = base.land().toString().split( '_' )
			while( parts.length < 3 ) parts.push( '' )
			parts.push( this.str.replace( /^___/, '' ) )
			
			return new $giper_baza_link( parts.join( '_' ) )
		}
		
		mix( mixin: Uint8Array< ArrayBuffer > | $giper_baza_link ) {
			if( mixin instanceof $giper_baza_link ) mixin = mixin.toBin()
			const mix = this.toBin().slice()
			for( let i = 0; i < mix.length; ++i ) mix[i] ^= mixin[i]
			return mix
		}
	
	}
	
	let _base = $giper_baza_link.hole
	export function $giper_baza_link_base< Res >( base: $giper_baza_link, task: ()=> Res ): Res {
		
		const prev = _base
		_base = base
		
		try {
			return task()
		} finally {
			_base = prev
		}
		
	}
	
}
